/**
 * @module services/itemActionsService
 * @description High-level item action service
 *
 * Provides user-facing item operations with:
 * - Notification feedback
 * - Error handling
 * - Clipboard operations
 * - Export functionality
 *
 * @example
 * import { itemActions } from '@services/itemActionsService';
 *
 * // Copy item title to clipboard
 * await itemActions.copyTitle(item);
 *
 * // Delete item with confirmation
 * await itemActions.deleteItem(libraryId, 'user', item);
 */

import { createLogger } from './loggerService';
import { notify } from './notificationService';
import { zoteroService } from './zoteroService';
import { removeItem, removeItems } from '@stores/itemStore';
import type {
  Item,
  ItemData,
  CreateItemInput,
  UpdateItemInput,
  LibraryType,
  ServiceResult,
  ActionResult,
} from '@types';

const log = createLogger('item');

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get item display title
 */
function getItemTitle(item: Item): string {
  return item.data.title || item.data.name || 'Untitled';
}

/**
 * Get item URL (if available)
 */
function getItemUrl(item: Item): string | null {
  return item.data.url || item.data.DOI ? `https://doi.org/${item.data.DOI}` : null;
}

/**
 * Format creators as string
 */
function formatCreators(item: Item): string {
  const creators = item.data.creators || [];
  if (creators.length === 0) return '';

  return creators
    .map((c) => {
      if (c.name) return c.name;
      return [c.lastName, c.firstName].filter(Boolean).join(', ');
    })
    .join('; ');
}

/**
 * Copy text to clipboard
 */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    log.error('Clipboard write failed', { error: String(error) });

    // Fallback for older browsers
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      return true;
    } catch {
      return false;
    }
  }
}

// =============================================================================
// ITEM ACTIONS SERVICE
// =============================================================================

/**
 * Item Actions Service
 */
class ItemActionsService {
  // ===========================================================================
  // CLIPBOARD OPERATIONS
  // ===========================================================================

  /**
   * Copy item title to clipboard
   */
  async copyTitle(item: Item): Promise<ActionResult> {
    const title = getItemTitle(item);
    const success = await copyToClipboard(title);

    if (success) {
      notify.success('Title copied to clipboard');
      log.debug('Title copied', { key: item.key, title });
      return { success: true, message: 'Title copied to clipboard' };
    }

    notify.error('Failed to copy title');
    return { success: false, message: 'Clipboard operation failed' };
  }

  /**
   * Copy item URL to clipboard
   */
  async copyUrl(item: Item): Promise<ActionResult> {
    const url = getItemUrl(item);

    if (!url) {
      notify.warning('No URL available for this item');
      return { success: false, message: 'No URL available' };
    }

    const success = await copyToClipboard(url);

    if (success) {
      notify.success('URL copied to clipboard');
      log.debug('URL copied', { key: item.key, url });
      return { success: true, message: 'URL copied to clipboard' };
    }

    notify.error('Failed to copy URL');
    return { success: false, message: 'Clipboard operation failed' };
  }

  /**
   * Copy formatted citation to clipboard
   */
  async copyCitation(item: Item, format: 'apa' | 'mla' | 'chicago' = 'apa'): Promise<ActionResult> {
    // Simple citation formatting (could be enhanced with CSL processor)
    const title = getItemTitle(item);
    const creators = formatCreators(item);
    const year = item.data.date ? new Date(item.data.date).getFullYear() : '';

    let citation = '';
    switch (format) {
      case 'apa':
        citation = `${creators} (${year}). ${title}.`;
        break;
      case 'mla':
        citation = `${creators}. "${title}." ${year}.`;
        break;
      case 'chicago':
        citation = `${creators}. "${title}." ${year}.`;
        break;
    }

    const success = await copyToClipboard(citation);

    if (success) {
      notify.success('Citation copied to clipboard');
      log.debug('Citation copied', { key: item.key, format });
      return { success: true, message: 'Citation copied to clipboard' };
    }

    notify.error('Failed to copy citation');
    return { success: false, message: 'Clipboard operation failed' };
  }

  // ===========================================================================
  // CRUD OPERATIONS
  // ===========================================================================

  /**
   * Create a new item
   */
  async createItem(
    libraryId: number,
    libraryType: LibraryType,
    input: CreateItemInput
  ): Promise<ServiceResult<Item>> {
    log.info('Creating item', { libraryId, itemType: input.itemType });

    const result = await zoteroService.createItem(libraryId, libraryType, input);

    if (result.success) {
      notify.success('Item created successfully');
      log.info('Item created', { key: result.data.key });
    } else {
      notify.error(`Failed to create item: ${result.error}`);
    }

    return result;
  }

  /**
   * Update an item
   */
  async updateItem(
    libraryId: number,
    libraryType: LibraryType,
    itemKey: string,
    input: UpdateItemInput
  ): Promise<ServiceResult<Item>> {
    log.info('Updating item', { libraryId, itemKey });

    const result = await zoteroService.updateItem(libraryId, libraryType, itemKey, input);

    if (result.success) {
      notify.success('Item updated successfully');
      log.info('Item updated', { key: itemKey });
    } else {
      notify.error(`Failed to update item: ${result.error}`);
    }

    return result;
  }

  /**
   * Delete an item (move to trash)
   */
  async deleteItem(
    libraryId: number,
    libraryType: LibraryType,
    item: Item
  ): Promise<ServiceResult<void>> {
    const title = getItemTitle(item);
    log.info('Deleting item', { key: item.key, title });

    const result = await zoteroService.deleteItem(libraryId, libraryType, item.key);

    if (result.success) {
      // Remove item from store immediately for responsive UI
      removeItem(item.key);
      notify.success(`"${title}" moved to trash`);
      log.info('Item deleted', { key: item.key });
    } else {
      notify.error(`Failed to delete item: ${result.error}`);
    }

    return result;
  }

  /**
   * Delete multiple items
   */
  async deleteItems(
    libraryId: number,
    libraryType: LibraryType,
    items: Item[]
  ): Promise<ServiceResult<void>> {
    const itemKeys = items.map((i) => i.key);
    log.info('Deleting items', { count: items.length });

    const result = await zoteroService.deleteItems(libraryId, libraryType, itemKeys);

    if (result.success) {
      // Remove items from store immediately for responsive UI
      removeItems(itemKeys);
      notify.success(`${items.length} items moved to trash`);
      log.info('Items deleted', { count: items.length });
    } else {
      notify.error(`Failed to delete items: ${result.error}`);
    }

    return result;
  }

  // ===========================================================================
  // DUPLICATE
  // ===========================================================================

  /**
   * Duplicate an item
   */
  async duplicateItem(
    libraryId: number,
    libraryType: LibraryType,
    item: Item
  ): Promise<ServiceResult<Item>> {
    const title = getItemTitle(item);
    log.info('Duplicating item', { key: item.key, title });

    // Create new item with same data (without key/version)
    const { key, version, itemType, ...rest } = item.data;
    const input: CreateItemInput = {
      itemType,
      ...rest,
      title: `${title} (copy)`,
    };

    const result = await zoteroService.createItem(libraryId, libraryType, input);

    if (result.success) {
      notify.success(`"${title}" duplicated`);
      log.info('Item duplicated', { originalKey: item.key, newKey: result.data.key });
    } else {
      notify.error(`Failed to duplicate item: ${result.error}`);
    }

    return result;
  }

  // ===========================================================================
  // EXPORT
  // ===========================================================================

  /**
   * Export item as BibTeX
   */
  exportAsBibtex(item: Item): string {
    const title = getItemTitle(item);
    const creators = item.data.creators || [];
    const year = item.data.date ? new Date(item.data.date).getFullYear() : '';
    const key = item.key.toLowerCase();

    // Map item type to BibTeX type
    const typeMap: Record<string, string> = {
      book: 'book',
      journalArticle: 'article',
      thesis: 'phdthesis',
      conferencePaper: 'inproceedings',
      bookSection: 'incollection',
      report: 'techreport',
      webpage: 'misc',
    };

    const bibtexType = typeMap[item.data.itemType] || 'misc';

    const authors = creators
      .filter((c) => c.creatorType === 'author')
      .map((c) => (c.name ? c.name : `${c.lastName}, ${c.firstName}`))
      .join(' and ');

    const fields: string[] = [];
    if (authors) fields.push(`  author = {${authors}}`);
    fields.push(`  title = {${title}}`);
    if (year) fields.push(`  year = {${year}}`);
    if (item.data.publisher) fields.push(`  publisher = {${item.data.publisher}}`);
    if (item.data.publicationTitle) fields.push(`  journal = {${item.data.publicationTitle}}`);
    if (item.data.volume) fields.push(`  volume = {${item.data.volume}}`);
    if (item.data.issue) fields.push(`  number = {${item.data.issue}}`);
    if (item.data.pages) fields.push(`  pages = {${item.data.pages}}`);
    if (item.data.DOI) fields.push(`  doi = {${item.data.DOI}}`);
    if (item.data.ISBN) fields.push(`  isbn = {${item.data.ISBN}}`);
    if (item.data.url) fields.push(`  url = {${item.data.url}}`);

    return `@${bibtexType}{${key},\n${fields.join(',\n')}\n}`;
  }

  /**
   * Export item as RIS
   */
  exportAsRis(item: Item): string {
    const lines: string[] = [];

    // Map item type to RIS type
    const typeMap: Record<string, string> = {
      book: 'BOOK',
      journalArticle: 'JOUR',
      thesis: 'THES',
      conferencePaper: 'CPAPER',
      bookSection: 'CHAP',
      report: 'RPRT',
      webpage: 'ELEC',
    };

    const risType = typeMap[item.data.itemType] || 'GEN';
    lines.push(`TY  - ${risType}`);

    // Authors
    const creators = item.data.creators || [];
    for (const creator of creators) {
      if (creator.creatorType === 'author') {
        const name = creator.name || `${creator.lastName}, ${creator.firstName}`;
        lines.push(`AU  - ${name}`);
      }
    }

    // Title
    if (item.data.title) lines.push(`TI  - ${item.data.title}`);

    // Year
    if (item.data.date) {
      const year = new Date(item.data.date).getFullYear();
      lines.push(`PY  - ${year}`);
    }

    // Journal/Book
    if (item.data.publicationTitle) lines.push(`JO  - ${item.data.publicationTitle}`);
    if (item.data.publisher) lines.push(`PB  - ${item.data.publisher}`);

    // Volume/Issue/Pages
    if (item.data.volume) lines.push(`VL  - ${item.data.volume}`);
    if (item.data.issue) lines.push(`IS  - ${item.data.issue}`);
    if (item.data.pages) lines.push(`SP  - ${item.data.pages}`);

    // Identifiers
    if (item.data.DOI) lines.push(`DO  - ${item.data.DOI}`);
    if (item.data.ISBN) lines.push(`SN  - ${item.data.ISBN}`);
    if (item.data.url) lines.push(`UR  - ${item.data.url}`);

    // Abstract
    if (item.data.abstractNote) lines.push(`AB  - ${item.data.abstractNote}`);

    lines.push('ER  - ');

    return lines.join('\n');
  }

  /**
   * Download item export
   */
  async downloadExport(item: Item, format: 'bibtex' | 'ris'): Promise<ActionResult> {
    try {
      const content = format === 'bibtex' ? this.exportAsBibtex(item) : this.exportAsRis(item);

      const filename = `${item.key}.${format === 'bibtex' ? 'bib' : 'ris'}`;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      notify.success(`Exported as ${format.toUpperCase()}`);
      log.debug('Item exported', { key: item.key, format });
      return { success: true, message: `Exported as ${format.toUpperCase()}` };
    } catch (error) {
      notify.error('Export failed');
      log.error('Export failed', { error: String(error) });
      return { success: false, message: String(error) };
    }
  }

  // ===========================================================================
  // OPEN EXTERNAL
  // ===========================================================================

  /**
   * Open item URL in new tab
   */
  openUrl(item: Item): ActionResult {
    const url = getItemUrl(item);

    if (!url) {
      notify.warning('No URL available for this item');
      return { success: false, message: 'No URL available' };
    }

    window.open(url, '_blank', 'noopener,noreferrer');
    log.debug('Opened URL', { key: item.key, url });
    return { success: true, message: 'URL opened' };
  }

  /**
   * Open item in Zotero web library
   */
  openInZotero(libraryId: number, libraryType: LibraryType, item: Item): ActionResult {
    const prefix = libraryType === 'user' ? 'users' : 'groups';
    const url = `https://www.zotero.org/${prefix}/${libraryId}/items/${item.key}`;

    window.open(url, '_blank', 'noopener,noreferrer');
    log.debug('Opened in Zotero', { key: item.key, url });
    return { success: true, message: 'Opened in Zotero' };
  }
}

// =============================================================================
// EXPORTS
// =============================================================================

/** Singleton instance */
export const itemActions = new ItemActionsService();

/** Export class for testing */
export { ItemActionsService };

export default itemActions;
